<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useWoowCrm, customerLabel } from 'dashboard/composables/useWoowCrm';
import WoowCrmAPI from 'dashboard/api/integrations/woowCrm';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  conversationId: { type: [Number, String], required: true },
});

const { t } = useI18n();
const conversationId = computed(() => props.conversationId);
const { linkedCustomer, linkCustomer, unlinkCustomer } =
  useWoowCrm(conversationId);

const dialogRef = ref(null);
const query = ref('');
const results = ref([]);
const isSearching = ref(false);
const isSaving = ref(false);
let searchTimer = null;

const search = async () => {
  const term = query.value.trim();
  isSearching.value = true;
  try {
    const { data } = await WoowCrmAPI.searchUsers(term);
    results.value = data.data ?? [];
  } catch (error) {
    useAlert(
      error?.response?.data?.error || t('WOOW_CRM.LINK_CUSTOMER.SEARCH_ERROR')
    );
  } finally {
    isSearching.value = false;
  }
};

watch(query, () => {
  clearTimeout(searchTimer);
  searchTimer = setTimeout(search, 350);
});

const handleLink = async user => {
  isSaving.value = true;
  try {
    await linkCustomer(user.id);
    useAlert(t('WOOW_CRM.LINK_CUSTOMER.LINKED', { name: customerLabel(user) }));
    dialogRef.value.close();
  } catch (error) {
    useAlert(error?.response?.data?.error || t('WOOW_CRM.LINK_CUSTOMER.ERROR'));
  } finally {
    isSaving.value = false;
  }
};

const handleUnlink = async () => {
  isSaving.value = true;
  try {
    await unlinkCustomer();
    useAlert(t('WOOW_CRM.LINK_CUSTOMER.UNLINKED'));
    dialogRef.value.close();
  } catch (error) {
    useAlert(error?.response?.data?.error || t('WOOW_CRM.LINK_CUSTOMER.ERROR'));
  } finally {
    isSaving.value = false;
  }
};

const userSubtitle = user =>
  [user.user_id, user.phone_number, user.email].filter(Boolean).join(' · ');

const open = () => {
  query.value = '';
  results.value = [];
  search();
  dialogRef.value.open();
};

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="t('WOOW_CRM.LINK_CUSTOMER.TITLE')"
    :description="t('WOOW_CRM.LINK_CUSTOMER.DESCRIPTION')"
    :show-cancel-button="false"
    :show-confirm-button="false"
    width="lg"
  >
    <div class="flex flex-col gap-4">
      <div
        v-if="linkedCustomer"
        class="flex items-center justify-between gap-3 p-3 rounded-lg bg-n-alpha-2"
      >
        <div class="flex flex-col min-w-0">
          <span class="text-sm font-medium truncate text-n-slate-12">
            {{ customerLabel(linkedCustomer) }}
          </span>
          <span class="text-xs truncate text-n-slate-11">
            {{ linkedCustomer.user_id }}
          </span>
        </div>
        <Button
          variant="faded"
          color="ruby"
          size="sm"
          :is-loading="isSaving"
          :label="t('WOOW_CRM.LINK_CUSTOMER.UNLINK')"
          @click="handleUnlink"
        />
      </div>

      <Input
        v-model="query"
        :placeholder="t('WOOW_CRM.LINK_CUSTOMER.SEARCH_PLACEHOLDER')"
        autofocus
      />

      <div class="flex flex-col max-h-80 overflow-y-auto">
        <p v-if="isSearching" class="py-6 text-sm text-center text-n-slate-11">
          {{ t('WOOW_CRM.LINK_CUSTOMER.SEARCHING') }}
        </p>
        <p
          v-else-if="!results.length"
          class="py-6 text-sm text-center text-n-slate-11"
        >
          {{ t('WOOW_CRM.LINK_CUSTOMER.NO_RESULTS') }}
        </p>
        <template v-else>
          <button
            v-for="user in results"
            :key="user.id"
            type="button"
            class="flex items-center justify-between gap-3 px-3 py-2 text-left rounded-lg hover:bg-n-alpha-2"
            @click="handleLink(user)"
          >
            <span class="flex flex-col min-w-0">
              <span class="text-sm truncate text-n-slate-12">
                {{ customerLabel(user) }}
              </span>
              <span class="text-xs truncate text-n-slate-11">
                {{ userSubtitle(user) }}
              </span>
            </span>
          </button>
        </template>
      </div>
    </div>
    <template #footer />
  </Dialog>
</template>
